import type React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { shouldShowApplyButton } from 'app/shared/utils/cometUtils';
import { useContactFormAction } from '../components/features/listings/ContactForm/useContactFormAction';
import { useTourAction } from '../components/features/listings/TourForm/useTourAction';
import { useApplyAction } from '../components/features/listings/ApplyForm/useApplyAction';
import { MessageIcon } from '../components/ui/icons/MessageIcon';
import { CalendarIcon } from '../components/ui/icons/CalendarIcon';

export type ListingCTAId = 'contact' | 'tour' | 'apply';

export type ListingCTA = {
  id: ListingCTAId;
  label: string;
  ariaLabel: string;
  icon: React.ReactNode;
  onClick: () => void;
  emphasis: 'filled' | 'outlined';
};

type UseListingCTAsResult = {
  ctas: Array<ListingCTA>;
  overlays: React.ReactNode;
};

export const useListingCTAs = (): UseListingCTAsResult => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const { openContactForm, contactFormOverlay } = useContactFormAction();
  const { openTour, tourOverlay } = useTourAction();
  const { openApply, applyOverlay } = useApplyAction();

  const overlays = (
    <>
      {contactFormOverlay}
      {tourOverlay}
      {applyOverlay}
    </>
  );

  if (!listing || !listing.active) {
    return { ctas: [], overlays };
  }

  const ctas: Array<ListingCTA> = [];

  ctas.push({
    id: 'contact',
    label: 'Contact',
    ariaLabel: 'Contact property',
    icon: <MessageIcon />,
    onClick: openContactForm,
    emphasis: 'filled',
  });

  const providers = listing.details?.instantTourProviders;
  const hasTourProviders = !!(
    providers?.inPersonProvider ||
    providers?.liveVideoProvider ||
    providers?.selfGuidedProvider
  );

  if (hasTourProviders) {
    ctas.push({
      id: 'tour',
      label: 'Tour',
      ariaLabel: 'Tour',
      icon: <CalendarIcon />,
      onClick: openTour,
      emphasis: 'outlined',
    });
  }

  if (shouldShowApplyButton(listing.rentalApplicationStatus)) {
    ctas.push({
      id: 'apply',
      label: 'Apply',
      ariaLabel: 'Apply now',
      icon: null,
      onClick: openApply,
      emphasis: 'outlined',
    });
  }

  return { ctas, overlays };
};
